import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

// Sequential row-by-row import (see BulkImportModal) is not meant for
// arbitrarily large pastes - this just keeps a bad file from silently
// hanging the import loop for minutes.
export const MAX_ROWS = 500;

// Yes/No, Y/N, true/false, 1/0 - case-insensitive. Anything else (including
// blank) reads as "No", matching the manual form's unchecked-checkbox default.
export const parseYesNo = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "yes" || s === "y" || s === "true" || s === "1";
};

const norm = (s) => String(s ?? "").trim().toLowerCase();

// Builds an .xlsx with an empty data sheet (header row only, ready for data
// entry) plus an "Instructions" sheet documenting each column with an
// example row - keeps the data sheet free of a sample row someone might
// forget to delete before uploading.
//
// columns[].dropdown, if given (a non-empty array of allowed display
// strings, e.g. "D - 55,001-56,000" per weight category, or ["Yes", "No"]),
// gets an Excel data-validation dropdown applied to every data row up front
// (row 2 through MAX_ROWS+1) - so the whole column already has the dropdown
// before anyone fills anything in, not just row 2. That also answers "how
// do I set the same value for 50 rows without picking it 50 times": once
// the dropdown is on the full range, Excel's own fill handle (drag the
// small square at the bottom-right of a filled cell down through the rest)
// or select-the-range-then-type-then-Ctrl+Enter both fill many cells at
// once while keeping each cell's dropdown intact - nothing to configure
// per row on our side.
//
// The list items live on a hidden "Lists" sheet (one column per dropdown
// column) and the validation formula references that range, rather than an
// inline comma-separated string - Excel's inline list source is capped at
// 255 characters, which a couple dozen "CODE - description" style items
// blows past. columns[].parse is expected to turn a picked "CODE - ..."
// string (or a plain "CODE" someone typed by hand) back into just the code.
//
// Uses ExcelJS rather than the "xlsx" package (used elsewhere in this file
// for reading) because the free "xlsx" build cannot write data validations
// - only ExcelJS can produce a real in-cell dropdown here.
export async function downloadTemplate({ filename, sheetName, columns, templateId }) {
  const wb = new ExcelJS.Workbook();

  const dataSheet = wb.addWorksheet(sheetName);
  dataSheet.columns = columns.map((c) => ({
    header: c.header,
    width: Math.max(c.header.length, (c.example || "").length, 14),
  }));

  // Stamps this workbook with which filing type's table it was generated
  // for, so parseUploadedFile can positively identify a wrong-template
  // upload (e.g. the Original Return template into the Mileage Exceeded
  // uploader) instead of relying on column-header overlap alone - several
  // of these templates share header text verbatim (VIN, Weight category,
  // Logging), so a header-only check lets the wrong file slip through with
  // its non-matching columns silently coming back blank. Hidden (not just
  // unlisted) the same way the "Lists" dropdown-source sheet below is, since
  // this is metadata, not something a filer should see or edit. Added right
  // after the data sheet so wb.SheetNames[0] (what parseUploadedFile reads
  // for actual row data) is unaffected.
  if (templateId) {
    const metaSheet = wb.addWorksheet("__meta__");
    metaSheet.state = "veryHidden";
    metaSheet.getCell("A1").value = templateId;
  }

  const dropdownColumns = columns.filter((c) => c.dropdown && c.dropdown.length);
  let listsSheet;
  if (dropdownColumns.length) {
    listsSheet = wb.addWorksheet("Lists");
    listsSheet.state = "veryHidden"; // reference data only, not meant to be browsed
  }

  const lastDataRow = 1 + MAX_ROWS;
  columns.forEach((c, i) => {
    if (!c.dropdown || !c.dropdown.length) return;
    const dataCol = dataSheet.getColumn(i + 1).letter;
    const listCol = listsSheet.getColumn(i + 1).letter;
    c.dropdown.forEach((value, idx) => {
      listsSheet.getCell(idx + 1, i + 1).value = value;
    });
    const rangeRef = `'Lists'!$${listCol}$1:$${listCol}$${c.dropdown.length}`;
    for (let row = 2; row <= lastDataRow; row++) {
      dataSheet.getCell(`${dataCol}${row}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [rangeRef],
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: "Not a valid option",
        error: "Pick one of the dropdown options for this column.",
      };
    }
  });

  const instructionsSheet = wb.addWorksheet("Instructions");
  instructionsSheet.columns = [{ width: 26 }, { width: 12 }, { width: 60 }, { width: 20 }];
  instructionsSheet.addRow(["Column", "Required?", "Description", "Example"]);
  columns.forEach((c) =>
    instructionsSheet.addRow([c.header, c.required ? "Required" : "Optional", c.description || "", c.example ?? ""])
  );
  instructionsSheet.addRow([]);
  instructionsSheet.addRow(["Example filled-in row (for reference only - the data sheet starts empty):"]);
  instructionsSheet.addRow(columns.map((c) => c.example ?? ""));

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const WRONG_TEMPLATE_MESSAGE = "The selected file does not match the required template for this filing type.";

// Reads an uploaded workbook's first sheet and maps each row's cells onto
// columns[].key by matching columns[].header (trimmed/lowercased) against
// the sheet's own header row - tolerant of minor header edits (extra
// spaces, case) though not of reordered/renamed columns beyond that.
//
// templateId, if given, must match the hidden "__meta__" sheet's A1 value
// downloadTemplate stamped into this exact workbook (see there) - this is
// the primary defense against uploading one filing type's template into
// another's uploader, since several templates share header text verbatim
// (VIN, Weight category, Logging) and a header-only check would let those
// through with the non-matching columns silently blank. The required-header
// check below is kept as a second, independent check (also catches
// hand-edited files, or files from before this identifier existed).
export function parseUploadedFile(file, columns, templateId) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file. Please try again."));
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) throw new Error("That file doesn't have any sheets.");

        if (templateId) {
          const metaSheet = wb.Sheets["__meta__"];
          const metaValue = metaSheet ? XLSX.utils.sheet_to_json(metaSheet, { header: 1 })?.[0]?.[0] : undefined;
          if (metaValue !== templateId) {
            throw new Error(WRONG_TEMPLATE_MESSAGE);
          }
        }

        const sheet = wb.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

        if (!raw.length) {
          resolve({ rows: [] });
          return;
        }

        const fileHeaders = Object.keys(raw[0]);
        const headerMap = {}; // column.key -> matching header text actually found in the file
        columns.forEach((c) => {
          const match = fileHeaders.find((h) => norm(h) === norm(c.header));
          if (match) headerMap[c.key] = match;
        });

        const missingRequired = columns.some((c) => c.required && headerMap[c.key] == null);
        if (Object.keys(headerMap).length === 0 || missingRequired) {
          throw new Error(
            templateId
              ? WRONG_TEMPLATE_MESSAGE
              : "This file doesn't look like the template for this table — please download a fresh copy and fill that in."
          );
        }

        const nonBlank = raw.filter((r) => fileHeaders.some((h) => String(r[h] ?? "").trim() !== ""));
        if (nonBlank.length > MAX_ROWS) {
          throw new Error(`This file has ${nonBlank.length} rows — please split it into batches of ${MAX_ROWS} or fewer.`);
        }

        const rows = nonBlank.map((r) => {
          const values = {};
          columns.forEach((c) => {
            const cell = headerMap[c.key] != null ? r[headerMap[c.key]] : "";
            values[c.key] = c.parse ? c.parse(cell) : String(cell ?? "").trim();
          });
          return values;
        });

        resolve({ rows });
      } catch (err) {
        reject(err instanceof Error ? err : new Error("Couldn't read that file. Please try again."));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

// Same field-error shape TrucksStep/VinCorrectionStep already parse
// (err.body.error.errors[].message), collapsed to one line for the bulk
// import results list instead of per-field inline state.
export function apiErrorMessage(err, fallback = "Couldn't save this row.") {
  const fieldErrors = err?.body?.error?.errors;
  if (Array.isArray(fieldErrors) && fieldErrors.length) {
    return fieldErrors.map((fe) => fe.message || fe.field).filter(Boolean).join(" ");
  }
  return err?.message || fallback;
}
